import { adminEmail } from '@/shared/constants';
import { type DriveFile, type Permission } from '@/shared/DriveTypes';
import {
  ArrowDropDown,
  CancelOutlined,
  ChevronLeft,
  Close,
  LibraryAddOutlined,
  PersonAddAlt1Outlined,
  Public,
} from '@mui/icons-material';
import {
  Avatar,
  Checkbox,
  FormControl,
  FormControlLabel,
  Input,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import cx from 'classnames';
import { bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import * as app from '~/app.css';
import * as buttonStyles from '~/elements/Buttons/Button.css';
import CopyToClipboard, { CopyToClipBoardButton } from '~/elements/CopyToClipboard/CopyToClipboard';
import { tooltipSlotProps, tooltipSlots } from '~/elements/Tooltip';
import { darkTheme, lightTheme } from '~/theme.css';
import { DEV, isBun, LEGACY, siteURL, tooltipEnterDelay } from '~/utils/constants';
import gup from '~/utils/gup';
import { useAuth, useContacts, useMutatePermission, usePermissions } from '~/utils/hooks/hooks';
import { useTheme } from '~/utils/store';
import { type Contact } from '~/utils/types';
import {
  focusInput,
  isAFolder,
  legacyClick,
  ls,
  onKeyDown,
  reg,
  rem,
  scrollToBottom,
  toQueryString,
  toTitleCase,
} from '~/utils/utils';
import { Title } from '../FileModal/FileModal';
import * as modalStyles from '../FileModal/FileModal.css';
import { SyncStatus } from '../Sidebar/SyncStatus';
import * as treeStyles from '../Tree/Tree.css';
import * as styles from './ShareModal.css';
import {
  contactsMenuHeight,
  getEmailPostfixes,
  getFilteredContacts,
  getPermissionDisabled,
  getRoles,
  getWidth,
} from './shareModalUtils';

export const submitButtonText = 'Invite';

const ButtonSubmit = (props: React.ButtonHTMLAttributes<HTMLButtonElement> = {}) => (
  <button type="submit" className={buttonStyles.primary} {...props}>
    &nbsp;{submitButtonText}&nbsp;
  </button>
);

type Props = {
  onCancel: (e: React.MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void;
  file: DriveFile | undefined;
  wiki: DriveFile;
  wikis: DriveFile[];
  files: DriveFile[];
  style?: React.CSSProperties;
  popover?: boolean;
  // Legacy
  updateFile?: ((payload: Partial<DriveFile>) => void) | undefined;
};

export const ShareModal = ({ onCancel, file, wiki, wikis, files, style, popover, updateFile }: Props) => {
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [notify, setNotify] = useState(ls.get<boolean>('wiki.notify') ?? false);
  const [isValid, setIsValid] = useState(false);
  const [value, setValue] = useState('');
  const [selected, setSelected] = useState(true);
  const [pendingPermissions, setPendingPermissions] = useState<Permission[]>([]);
  const [activePermission, setActivePermission] = useState<Permission | null>(null);
  const toBeAddedMenu = usePopupState({ variant: 'popover' });
  const accessMenu = usePopupState({ variant: 'popover' });
  const contactsMenu = usePopupState({ variant: 'popover' });
  const inputRef = useRef<HTMLInputElement>(null);
  const [shareMultipleOpen, setShareMultipleOpen] = useState(false);
  const [selectedWikiIds, setSelectedWikiIds] = useState<string[]>([]);
  const { data: permissions } = usePermissions(file);
  const { data: user } = useAuth();
  const { roleMap, roles } = getRoles(file, user);
  const { createPermission, updatePermission, removePermission } = useMutatePermission({ file, files, wiki });
  const userPermission = file?.permissions?.find((p) => p.type === 'user' && p.emailAddress === user?.email);
  const theme = useTheme();

  useEffect(() => focusInput(inputRef.current), [addUserOpen]);

  const { data: contacts = [] } = useContacts(addUserOpen);

  // Get contacts from other wikis
  const wikiContacts: Contact[] =
    wikis
      ?.flatMap((wiki) => wiki.permissions ?? [])
      .filter((p): p is Extract<Permission, { emailAddress: string }> => p.type === 'user')
      .map((o) => ({
        id: o.id,
        name: o.displayName,
        email: o.emailAddress,
        photoURL: o.photoLink,
      })) ?? [];

  const filteredContacts = getFilteredContacts({
    contacts,
    pendingPermissions,
    permissions,
    value,
    wikiContacts,
  });

  const emailPostfixes = getEmailPostfixes({ file, wikiContacts });

  const valuePostfix = value.split('@')[1];
  const suggestedPostfix = emailPostfixes.find((emailPostfix) =>
    valuePostfix ? emailPostfix.startsWith(valuePostfix) : false
  );
  const suggestedEmail = value.split('@')[0] + (suggestedPostfix ? `@${suggestedPostfix}` : '');

  const addPermissions = async (pendingPermissions: Permission | Permission[]) => {
    setPendingPermissions([]);
    const file = await createPermission(pendingPermissions, notify);
    if (LEGACY && file) {
      updateFile?.(file);
    }
  };

  const isPublic =
    (!file?.driveId && !permissions) || (permissions ? (permissions?.some((p) => p.type === 'anyone') ?? false) : true);
  const isContactsMenuOpen =
    contactsMenu.isOpen && value.length !== 0 && (filteredContacts.length === 0 ? /.+@.+/.test(value) : true);

  const { pathname } = window.location;
  const path =
    pathname === '/' || pathname === '/app' || pathname === '/app/page'
      ? LEGACY
        ? `/app/page/${file?.id ?? wiki.id}`
        : `/app/page/${wiki.id}/${file?.id ?? wiki.id}`
      : pathname;

  const url = LEGACY ? (gup('parentURL') ?? siteURL) : siteURL;
  const showPermissions =
    !isPublic ||
    file?.capabilities?.canEdit ||
    file?.permissions?.some((o) => o.type === 'user' && o.emailAddress === user?.email);

  const params = toQueryString({
    p: LEGACY ? ((gup('p') ?? isPublic) ? wiki.id : undefined) : undefined,
    wikis: shareMultipleOpen && selectedWikiIds.length > 0 ? selectedWikiIds.join(',') : undefined,
    invite: LEGACY && showPermissions && !isPublic ? 'true' : undefined,
  })?.replaceAll('%2C', ',');
  const inputValue = `${url}${path}${params ?? ''}`;
  const width = showPermissions
    ? getWidth((permissions?.length ?? 0) + (pendingPermissions?.length ?? 0))
    : getWidth(0);

  return (
    <div
      className={popover ? styles.popover : styles.modal}
      style={{
        zIndex: 1000,
        ...style,
        width,
        borderRadius: 10,
      }}
    >
      {!popover && (
        <button
          type="button"
          style={{
            width: 34,
            height: 34,
            position: 'absolute',
            top: -40,
            right: -4,
            padding: 0,
          }}
          className={buttonStyles.buttonCircular}
          onClick={onCancel}
        >
          <Close
            style={{
              color: '#fff',
              fontSize: 20,
            }}
          />
        </button>
      )}
      <form
        className={styles.form}
        style={{
          paddingTop: rem(20),
          paddingBottom: rem(32),
        }}
        onSubmit={(e) => {
          e.preventDefault();
          if (pendingPermissions.length === 0) throw new Error('No pending permissions');
          void addPermissions(pendingPermissions);
        }}
        onKeyDown={onKeyDown}
        aria-label="form"
      >
        <div
          className="flex flex-ac flex-jc-sb"
          style={{
            gap: rem(8),
          }}
        >
          <div className="flex flex-ac">
            <Title
              style={{
                marginBottom: 2,
                fontFamily: 'var(--font-title)',
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {shareMultipleOpen ? 'Share wikis' : file?.id === wiki.id ? 'Share' : `Share "${file?.name}"`}
            </Title>
            {LEGACY && (
              <div style={{ marginLeft: rem(2), marginTop: rem(2) }}>
                <SyncStatus />
              </div>
            )}
          </div>
          {shareMultipleOpen ? (
            <List
              onClick={(e) => {
                e.preventDefault();
                setShareMultipleOpen(false);
                setSelectedWikiIds([]);
              }}
              style={{
                marginRight: '.5rem',
              }}
            >
              <ListItem disablePadding disableGutters>
                <ListItemButton
                  className={styles.text}
                  style={{
                    fontSize: 14,
                    paddingLeft: rem(8),
                    borderRadius: 6,
                    fontFamily: 'var(--font-title)',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    marginTop: -1,
                    marginRight: '-.5rem',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronLeft
                    className={styles.text}
                    style={{
                      fontSize: 20,
                    }}
                    onClick={() => {
                      setShareMultipleOpen(false);
                      setSelectedWikiIds([]);
                    }}
                  />
                  <div style={{ marginTop: -2 }}>Back</div>
                </ListItemButton>
              </ListItem>
            </List>
          ) : (
            <List
              style={{
                marginRight: '.5rem',
                visibility: (file?.driveId && file.id === wiki.driveId) || !showPermissions ? 'hidden' : undefined,
              }}
            >
              <ListItem disablePadding disableGutters>
                <ListItemButton
                  className={styles.text}
                  style={{
                    fontSize: 14,
                    paddingLeft: rem(12),
                    paddingRight: rem(28),
                    borderRadius: 6,
                    fontFamily: 'var(--font-title)',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    marginTop: -1,
                    marginRight: '-.5rem',
                  }}
                  onClick={(e) => {
                    accessMenu.setAnchorEl(e.currentTarget);
                    accessMenu.open();
                  }}
                >
                  {isPublic ? 'Public' : 'Private'}
                </ListItemButton>
              </ListItem>
              <ArrowDropDown
                className={styles.arrow}
                style={{
                  position: 'absolute',
                  right: rem(-6),
                  top: 0,
                  bottom: 0,
                  margin: 'auto',
                  pointerEvents: 'none',
                }}
              />
            </List>
          )}
          <Menu
            {...bindMenu(accessMenu)}
            className={cx({
              [darkTheme]: theme === 'dark',
              [lightTheme]: theme === 'light',
            })}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'center',
            }}
            transitionDuration={{
              enter: 200,
              exit: 0,
            }}
            open={accessMenu.isOpen}
            onClose={() => accessMenu.close()}
            slotProps={{
              paper: {
                style: {
                  color: 'inherit',
                  boxShadow: 'rgba(0, 0, 0, 0.2) 2px 2px 8px 0',
                  borderRadius: 6,
                },
              },
            }}
          >
            <MenuItem
              selected={!isPublic}
              onClick={async () => {
                if (!isPublic) return accessMenu.close();
                // set to private
                // remove public permission
                const p = permissions?.find((p) => p.type === 'anyone');
                if (!p) throw new Error('permission not found');
                accessMenu.close();
                const file = await removePermission(p);
                if (LEGACY && file) {
                  updateFile?.({
                    ...file,
                    permissions: file.permissions?.filter((permission) => permission.id !== p.id) ?? [],
                  });
                }
              }}
              className={styles.text}
              style={{
                fontSize: 14,
                padding: `${rem(14)} ${rem(28)} ${rem(14)} ${rem(12)}`,
                fontFamily: 'var(--font-title)',
                fontWeight: 600,
                lineHeight: 1.25,
                marginRight: '-.5rem',
              }}
            >
              <ListItemText
                slotProps={{
                  primary: {
                    style: {
                      fontFamily: 'var(--font-title)',
                      fontWeight: 600,
                    },
                  },
                  secondary: {
                    style: {
                      color: 'inherit',
                      fontFamily: 'var(--font)',
                      letterSpacing: -0.01,
                    },
                  },
                }}
                primary="Private"
                secondary="Shared with specific people"
              />
            </MenuItem>
            <MenuItem
              className={styles.text}
              selected={isPublic}
              onClick={() => {
                if (isPublic) return accessMenu.close();
                // set to public
                // add public permission
                void addPermissions({
                  id: 'anyoneWithLink',
                  type: 'anyone',
                  role: 'reader',
                });
                accessMenu.close();
              }}
              style={{
                fontSize: 14,
                padding: `${rem(14)} ${rem(28)} ${rem(14)} ${rem(12)}`,
                fontFamily: 'var(--font-title)',
                fontWeight: 600,
                lineHeight: 1.25,
                marginRight: '-.5rem',
              }}
            >
              <ListItemText
                slotProps={{
                  primary: {
                    style: {
                      fontFamily: 'var(--font-title)',
                      fontWeight: 600,
                    },
                  },
                  secondary: {
                    style: {
                      color: 'inherit',
                      fontFamily: 'var(--font)',
                      letterSpacing: -0.01,
                    },
                  },
                }}
                primary="Public"
                secondary="Anyone with the link can access. No sign-in required."
              />
            </MenuItem>
          </Menu>
        </div>
        {shareMultipleOpen ? (
          <div
            style={{
              marginTop: '.5rem',
              marginBottom: '1rem',
              fontSize: 15,
            }}
          >
            Select wikis to generate a share link
          </div>
        ) : file?.id === wiki.id && showPermissions && file.capabilities?.canEdit ? (
          <div
            style={{
              marginTop: '.5rem',
              marginBottom: '1rem',
              fontSize: 15,
            }}
          >
            <Link to="/app/invite" className={styles.text} onClick={legacyClick}>
              Send an invite
            </Link>{' '}
            or {isPublic ? 'share this link' : 'add a new user'} to give access.
          </div>
        ) : null}
        <div
          className={cx([app.flex, app.alignCenter, app.relative])}
          style={{
            marginTop: 8,
            marginBottom: showPermissions ? 26 : 4,
          }}
        >
          <input
            aria-label="Share url"
            onClick={(e) => focusInput(e.target as HTMLInputElement)}
            readOnly
            value={inputValue}
            className={styles.inputReadonly}
            style={{
              paddingRight: wikis.length > 1 && file?.id === wiki.id && showPermissions ? undefined : '3rem',
            }}
          />
          <div
            style={{
              width: '2rem',
              height: '100%',
              position: 'absolute',
              right: 0,
            }}
          />
          {wikis.length > 1 && file?.id === wiki.id && showPermissions && (
            <Tooltip
              arrow
              title={shareMultipleOpen ? 'Close' : 'Share multiple wikis'}
              slotProps={tooltipSlotProps}
              slots={tooltipSlots}
              {...tooltipEnterDelay}
            >
              <button
                type="button"
                className={buttonStyles.buttonCircular}
                style={{
                  display: 'inline-flex',
                  width: 34,
                  height: 34,
                  right: 41,
                  position: 'absolute',
                }}
                onClick={() => {
                  setShareMultipleOpen(!shareMultipleOpen);
                }}
              >
                {!shareMultipleOpen && (
                  <LibraryAddOutlined
                    style={{
                      fontSize: '1.25rem',
                      cursor: 'pointer',
                      color: theme === 'light' ? '#7d7d7d' : '#fff',
                    }}
                  />
                )}
                {shareMultipleOpen && (
                  <CancelOutlined
                    style={{
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      color: theme === 'light' ? '#7d7d7d' : '#fff',
                      transform: 'rotate(180deg)',
                    }}
                  />
                )}
              </button>
            </Tooltip>
          )}
          <CopyToClipboard
            text={inputValue}
            containerStyle={{
              right: 7,
              position: 'absolute',
            }}
          />
        </div>

        {shareMultipleOpen && (
          <>
            <div className="flex flex-ac flex-jc">
              <FormControl style={{ width: '100%', marginTop: -4, marginBottom: '1rem' }}>
                <InputLabel
                  style={{
                    fontFamily: 'var(--font-title)',
                    marginLeft: -12,
                  }}
                  htmlFor="select-multiple"
                >
                  Select wikis
                </InputLabel>
                <Select
                  multiple
                  value={selectedWikiIds}
                  {...(isBun && { open: true })}
                  onChange={(e) => {
                    setSelectedWikiIds(e.target.value as string[]);
                  }}
                  input={<Input id="select-multiple" />}
                  renderValue={(selected) =>
                    selected.map((id) => wikis.find((wiki) => wiki.id === id)?.name).join(', ')
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 224,
                        width: 250,
                      },
                    },
                  }}
                >
                  {wikis.map((wiki) => (
                    <MenuItem key={wiki.id} value={wiki.id}>
                      <Checkbox checked={selectedWikiIds.includes(wiki.id)} />
                      <ListItemText primary={wiki.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div
              style={{
                paddingTop: 14,
                paddingBottom: 4,
              }}
              className={cx(app.flex, app.justifyCenter)}
            >
              <CopyToClipBoardButton disabled={selectedWikiIds.length <= 1} text={inputValue} />
            </div>
          </>
        )}
        {!shareMultipleOpen && permissions && showPermissions && (
          <h2
            className={treeStyles.subTitle}
            style={{
              marginTop: rem(10),
              marginBottom: rem(18),
            }}
          >
            WHO HAS ACCESS TO THIS {file?.id === wiki.id ? 'WIKI' : isAFolder(file) ? 'FOLDER' : 'FILE'}
          </h2>
        )}
        {/* permissions.map is here */}
        {!shareMultipleOpen &&
          showPermissions &&
          permissions
            ?.sort((a, b) => {
              // 'anyone' always comes first
              if (a.type === 'anyone') return -1;
              if (b.type === 'anyone') return 1;

              // admin always comes last
              if (a.type === 'user' && a.emailAddress === adminEmail) return 1;
              if (b.type === 'user' && b.emailAddress === adminEmail) return -1;

              // current user comes next (after 'anyone')
              if (user?.email && a.type === 'user' && a.emailAddress === user?.email) return -1;
              if (user?.email && b.type === 'user' && b.emailAddress === user?.email) return 1;

              return 0;
            })
            .map((permission) => {
              const isPermissionDisabled = getPermissionDisabled(file, permission, user, userPermission);
              return (
                <div
                  key={permission.id}
                  className="flex flex-ac flex-jc-sb"
                  style={{
                    marginBottom: 24,
                  }}
                >
                  <div
                    className="flex flex-ac flex-jc"
                    style={{
                      gap: rem(22),
                    }}
                  >
                    {(permission.type === 'user' || permission.type === 'group') && (
                      <>
                        <Avatar
                          alt={permission.displayName}
                          src={permission.emailAddress === adminEmail ? '/img/icon-80.png' : permission.photoLink}
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 38,
                            backgroundColor: wiki.properties?.color ?? '#638af5',
                            color: wiki.properties?.fontColor ?? undefined,
                          }}
                          {...(DEV && {
                            imgProps: {
                              referrerPolicy: 'no-referrer',
                            },
                          })}
                        >
                          {permission.displayName.charAt(0).toUpperCase()}
                        </Avatar>
                        <div className="flex flex-col flex-jc">
                          <div
                            className={styles.text}
                            style={{
                              fontWeight: 600,
                              fontSize: 16,
                              fontFamily: 'var(--font-title)',
                            }}
                          >
                            {reg.regex.isEmail.test(permission.displayName) || /\s/.test(permission.displayName)
                              ? permission.displayName
                              : toTitleCase(permission.displayName)}
                          </div>
                          <div
                            style={{
                              fontWeight: 500,
                              fontSize: 14,
                              fontFamily: 'var(--font-title)',
                              color: '#A4A8B0',
                              maxWidth: rem(200),
                              paddingRight: rem(8),
                            }}
                            className={app.ellipsis}
                          >
                            {permission.emailAddress}
                          </div>
                        </div>
                      </>
                    )}
                    {permission.type === 'domain' && (
                      <>
                        <Avatar
                          alt="Anyone with the link"
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 38,
                            backgroundColor: '#638AF5',
                          }}
                        >
                          {permission.displayName.charAt(0)}
                        </Avatar>
                        <div className="flex flex-col flex-jc">
                          <div
                            className={styles.text}
                            style={{
                              fontWeight: 600,
                              fontSize: 16,
                              fontFamily: 'var(--font-title)',
                            }}
                          >
                            {permission.displayName}
                          </div>
                          <div
                            style={{
                              fontWeight: 500,
                              fontSize: 14,
                              fontFamily: 'var(--font-title)',
                              color: '#A4A8B0',
                              maxWidth: rem(200),
                              paddingRight: rem(8),
                            }}
                          >
                            {permission.displayName}
                          </div>
                        </div>
                      </>
                    )}
                    {permission.type === 'anyone' && (
                      <>
                        <Avatar
                          alt="Anyone with the link"
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 38,
                            backgroundColor: '#638AF5',
                          }}
                        >
                          <Public />
                        </Avatar>
                        <div className="flex flex-col flex-jc">
                          <div
                            className={styles.text}
                            style={{
                              fontWeight: 600,
                              fontSize: 16,
                              fontFamily: 'var(--font-title)',
                            }}
                          >
                            Anyone with the link
                          </div>
                          <div
                            style={{
                              fontWeight: 500,
                              fontSize: 14,
                              fontFamily: 'var(--font-title)',
                              color: '#A4A8B0',
                              maxWidth: rem(200),
                              paddingRight: rem(8),
                            }}
                          >
                            No sign-in required
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <List>
                    <ListItemButton
                      disabled={isPermissionDisabled}
                      className={styles.text}
                      style={{
                        fontSize: 14,
                        paddingLeft: rem(12),
                        paddingRight: isPermissionDisabled ? rem(20) : rem(28),
                        fontFamily: 'var(--font-title)',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        marginTop: -1,
                        marginRight: '-.5rem',
                        borderRadius: 6,
                      }}
                      onClick={(e) => {
                        if (permission.role === 'owner') return;
                        setActivePermission(permission);
                        toBeAddedMenu.setAnchorEl(e.currentTarget);
                        toBeAddedMenu.open();
                      }}
                    >
                      {permission.type === 'user' && permission.emailAddress === adminEmail
                        ? 'Access'
                        : roleMap[permission.role]}
                    </ListItemButton>
                    {!isPermissionDisabled && (
                      <ArrowDropDown
                        style={{
                          position: 'absolute',
                          right: rem(-6),
                          top: 0,
                          bottom: 0,
                          margin: 'auto',
                          color: '#757575',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </List>
                </div>
              );
            })}
        <Menu
          {...bindMenu(toBeAddedMenu)}
          className={cx({
            [darkTheme]: theme === 'dark',
            [lightTheme]: theme === 'light',
          })}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transitionDuration={{
            enter: 200,
            exit: 0,
          }}
          open={activePermission !== null}
          onClose={() => setActivePermission(null)}
          slotProps={{
            paper: {
              style: {
                color: 'inherit',
                boxShadow: 'rgba(0, 0, 0, 0.2) 2px 2px 8px 0',
                borderRadius: 6,
              },
            },
          }}
        >
          <List style={{ padding: 0 }}>
            {roles.map(({ role, label, description }) => (
              <ListItemButton
                selected={activePermission?.role === role}
                key={role}
                onClick={async () => {
                  if (!activePermission) throw new Error('Active permission not found');
                  setActivePermission(null);
                  if (pendingPermissions.some((p) => p.id === activePermission.id)) {
                    setPendingPermissions(
                      pendingPermissions.map((p) => (p.id === activePermission.id ? { ...p, role } : p))
                    );
                  } else {
                    const file = await updatePermission({ ...activePermission, role });
                    if (LEGACY && file) {
                      updateFile?.(file);
                    }
                  }
                }}
                className={styles.text}
                style={{
                  fontSize: 14,
                  paddingLeft: rem(12),
                  paddingRight: rem(28),
                  fontFamily: 'var(--font-title)',
                  fontWeight: 600,
                  lineHeight: 1.25,
                  marginTop: -1,
                  marginRight: '-.5rem',
                }}
              >
                <ListItemText
                  slotProps={{
                    primary: {
                      style: {
                        fontFamily: 'var(--font-title)',
                        fontWeight: 600,
                      },
                    },
                    secondary: {
                      style: {
                        fontFamily: 'var(--font)',
                        letterSpacing: -0.01,
                      },
                    },
                  }}
                  primary={label}
                  secondary={description}
                />
              </ListItemButton>
            ))}
            <ListItemButton
              onClick={async () => {
                if (!activePermission) return;
                setActivePermission(null);
                if (pendingPermissions.some((p) => p.id === activePermission.id)) {
                  setPendingPermissions(pendingPermissions.filter((p) => p.id !== activePermission.id));
                } else {
                  const file = await removePermission(activePermission);
                  if (LEGACY && file) {
                    updateFile?.({
                      ...file,
                      permissions: file.permissions?.filter((p) => p.id !== activePermission.id) ?? [],
                    });
                  }
                }
              }}
            >
              <ListItemText
                slotProps={{
                  primary: {
                    style: {
                      fontFamily: 'var(--font-title)',
                      fontWeight: 600,
                    },
                  },
                  secondary: {
                    style: {
                      fontFamily: 'var(--font)',
                      letterSpacing: -0.01,
                    },
                  },
                }}
                primary="Remove"
                secondary="Remove this user"
              />
            </ListItemButton>
          </List>
        </Menu>
        {pendingPermissions?.length !== 0 && (
          <h4 className={treeStyles.subTitle}>
            TO BE <span style={{ letterSpacing: 1 }}>ADDED</span>
          </h4>
        )}
        {pendingPermissions.map((permission, i) => (
          <div
            key={i}
            style={{
              marginTop: 8,
              marginBottom: 24,
            }}
          >
            {permission.type === 'user' && (
              <>
                <div className="flex flex-ac flex-jc-sb">
                  <div
                    className="flex flex-ac"
                    style={{
                      gap: rem(22),
                    }}
                  >
                    <Avatar
                      alt={permission.displayName}
                      src={permission.photoLink}
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 38,
                      }}
                      {...(DEV && {
                        imgProps: {
                          referrerPolicy: 'no-referrer',
                        },
                      })}
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        className={styles.text}
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          fontFamily: 'var(--font-title)',
                        }}
                      >
                        {permission.displayName}
                      </div>
                      <div
                        className={app.ellipsis}
                        style={{
                          fontWeight: 500,
                          fontSize: 14,
                          fontFamily: 'var(--font-title)',
                          color: '#A4A8B0',
                          maxWidth: rem(200),
                          paddingRight: rem(8),
                        }}
                      >
                        {permission.emailAddress}
                      </div>
                    </div>
                  </div>
                  <List>
                    <ListItemButton
                      disabled={permission.role === 'owner'}
                      className={styles.text}
                      style={{
                        fontSize: 14,
                        paddingLeft: rem(12),
                        paddingRight: permission.role === 'owner' ? rem(22) : rem(28),
                        borderRadius: 6,
                        fontFamily: 'var(--font-title)',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        marginTop: -1,
                        marginRight: '-.5rem',
                      }}
                      onClick={(e) => {
                        if (permission.role === 'owner') return;
                        setActivePermission(permission);
                        toBeAddedMenu.setAnchorEl(e.currentTarget);
                        toBeAddedMenu.open();
                      }}
                    >
                      {roleMap[permission.role]}
                    </ListItemButton>
                    {permission.role !== 'owner' && (
                      <ArrowDropDown
                        style={{
                          position: 'absolute',
                          right: rem(-6),
                          top: 0,
                          bottom: 0,
                          margin: 'auto',
                          color: '#757575',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </List>
                </div>
              </>
            )}
          </div>
        ))}
        {!shareMultipleOpen && permissions && !addUserOpen && showPermissions && file?.capabilities?.canEdit && (
          <div
            style={{
              paddingTop: 16,
              paddingBottom: 8,
            }}
            className={cx(app.flex, app.justifyCenter)}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setAddUserOpen(true);
                scrollToBottom(e.currentTarget.closest('form'));
              }}
              className={buttonStyles.primaryWithIcon}
            >
              <PersonAddAlt1Outlined style={{ fontSize: 20 }} />
              Add a new user
            </button>
          </div>
        )}

        {addUserOpen && !shareMultipleOpen && (
          <div
            style={{
              paddingTop: 4,
            }}
          >
            <div
              className="flex flex-ac"
              style={{
                position: 'relative',
                gap: '.5rem',
              }}
            >
              <input
                type="email"
                ref={inputRef}
                value={value}
                placeholder="Invite people and groups"
                className={modalStyles.modalInput}
                style={{
                  fontFamily: 'var(--font-title)',
                  fontWeight: 600,
                  borderRadius: 6,
                  paddingRight: '3.4rem',
                }}
                onChange={(e) => {
                  const { value } = e.target;
                  setValue(value);
                  setSelected(true);
                  if (value.length === 0) {
                    contactsMenu.close();
                  } else {
                    contactsMenu.setAnchorEl(inputRef.current);
                    contactsMenu.open();
                  }
                  const { valid } = e.target.validity;
                  if (!valid && value.includes('@') && !value.startsWith('@') && e.target.value.endsWith('.')) {
                    if (isValid) setIsValid(false);
                    return;
                  }
                  setIsValid(/\..{2,}/.test(e.target.value) ? e.target.validity.valid : false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isContactsMenuOpen) {
                      setAddUserOpen(false);
                      setValue('');
                    }
                    contactsMenu.close();
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const firstMenuItem = document.querySelector<HTMLButtonElement>(
                      '[role="menu"] [role="button"]:first-child'
                    );
                    firstMenuItem?.focus();
                    setSelected(false);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const lastMenuItem = document.querySelector<HTMLButtonElement>(
                      '[role="menu"] [role="button"]:last-child'
                    );
                    lastMenuItem?.focus();
                    setSelected(false);
                    return;
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const firstMenuItem = document.querySelector<HTMLButtonElement>(
                      '[role="menu"] [role="button"]:first-child'
                    );
                    if (firstMenuItem && firstMenuItem.getAttribute('aria-disabled') !== 'true') {
                      contactsMenu.close();
                      firstMenuItem.click();
                      setValue('');
                      scrollToBottom(e.currentTarget.closest('form'));
                      return;
                    }
                  }
                }}
              />
              <Menu
                {...bindMenu(contactsMenu)}
                autoFocus={false}
                disableAutoFocus
                disableEnforceFocus
                transitionDuration={{
                  enter: 200,
                  exit: 0,
                }}
                open={isContactsMenuOpen}
                onClose={() => contactsMenu.close()}
                anchorOrigin={{
                  // Show below input if there's enough space, otherwise show above
                  get vertical() {
                    const bottom = inputRef.current?.getBoundingClientRect().bottom ?? 0;
                    if (bottom + contactsMenuHeight + 14 < document.body.clientHeight) return 'bottom';
                    return 'top';
                  },
                  horizontal: 'left',
                }}
                transformOrigin={{
                  // Show below input if there's enough space, otherwise show above
                  get vertical() {
                    const bottom = inputRef.current?.getBoundingClientRect().bottom ?? 0;
                    if (bottom + contactsMenuHeight + 14 < document.body.clientHeight) return 'top';
                    return 'bottom';
                  },
                  horizontal: 'left',
                }}
                slotProps={{
                  paper: {
                    style: {
                      get minWidth() {
                        return inputRef.current?.offsetWidth;
                      },
                      color: 'inherit',
                      boxShadow: 'rgba(0, 0, 0, 0.2) 2px 2px 8px 0',
                      borderRadius: 6,
                      maxHeight: contactsMenuHeight,
                      ...(!isValid &&
                        suggestedEmail.length !== value.length && {
                          cursor: 'not-allowed',
                        }),
                    },
                  },
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    inputRef.current?.focus();
                  }
                }}
              >
                {/* Email */}
                {filteredContacts.length === 0 && /.+@.+/.test(value) && (
                  <ListItemButton
                    className={styles.listItemButton}
                    selected={isValid || suggestedEmail.length > value.length}
                    disabled={!isValid && suggestedEmail.length < value.length}
                    style={{
                      gap: rem(22),
                      padding: `${rem(6)} ${rem(16)}`,
                    }}
                    onClick={() => {
                      const email = value.length > suggestedEmail.length ? value : suggestedEmail;
                      const newPermission: Permission = {
                        id: email,
                        role: 'writer',
                        type: 'user',
                        displayName: email,
                        emailAddress: email,
                        photoLink: '',
                      };
                      setPendingPermissions((permissions) => [...permissions, newPermission]);
                      contactsMenu.close();
                      setValue('');
                    }}
                  >
                    <Avatar
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 38,
                        backgroundColor: '#638AF5',
                      }}
                      {...(DEV && {
                        imgProps: {
                          referrerPolicy: 'no-referrer',
                        },
                      })}
                    >
                      {value.charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={value.length > suggestedEmail.length ? value : suggestedEmail}
                      secondary={value.length > suggestedEmail.length ? value : suggestedEmail}
                      slotProps={{
                        primary: {
                          style: {
                            fontFamily: 'var(--font-title)',
                            fontWeight: 600,
                            fontSize: 16,
                          },
                        },
                        secondary: {
                          style: {
                            fontFamily: 'var(--font-title)',
                            fontWeight: 500,
                            fontSize: 14,
                            color: '#A4A8B0',
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                )}

                {/* Contacts */}
                {filteredContacts?.map((contact, i) => (
                  <ListItemButton
                    style={{
                      gap: rem(22),
                      padding: `${rem(6)} ${rem(16)}`,
                    }}
                    selected={i === 0 && selected}
                    key={contact.id}
                    {...(i === 0 &&
                      ({
                        onKeyDown: (e) => {
                          if (filteredContacts.length === 1) {
                            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                          } else if (e.key !== 'ArrowUp') return;

                          e.preventDefault();
                          e.stopPropagation();
                          const prevSibling = e.currentTarget.previousElementSibling;
                          if (!prevSibling) {
                            inputRef.current?.focus();
                          }
                        },
                      } satisfies { onKeyDown: React.HTMLAttributes<HTMLDivElement>['onKeyDown'] }))}
                    {...(i !== 0 &&
                      i === filteredContacts.length - 1 &&
                      ({
                        onKeyDown: (e) => {
                          if (e.key !== 'ArrowDown') return;
                          e.preventDefault();
                          e.stopPropagation();
                          const nextSibling = e.currentTarget.nextElementSibling;
                          if (!nextSibling) {
                            inputRef.current?.focus();
                          }
                        },
                      } satisfies { onKeyDown: React.HTMLAttributes<HTMLDivElement>['onKeyDown'] }))}
                    onClick={() => {
                      const newPermission: Permission = {
                        id: contact.id,
                        role: 'writer',
                        type: 'user',
                        displayName: contact.name ?? contact.email ?? '',
                        emailAddress: contact.email ?? '',
                        photoLink: contact.photoURL ?? '',
                      };
                      setPendingPermissions((permissions) => [...permissions, newPermission]);
                      contactsMenu.close();
                      setValue('');
                    }}
                  >
                    <Avatar
                      alt={contact.name ?? ''}
                      src={contact.photoURL ?? ''}
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 38,
                      }}
                      {...(DEV && {
                        imgProps: {
                          referrerPolicy: 'no-referrer',
                        },
                      })}
                    >
                      {contact.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={
                        contact.name
                          ? reg.regex.isEmail.test(contact.name) || /\s/.test(contact.name)
                            ? contact.name
                            : toTitleCase(contact.name)
                          : ''
                      }
                      secondary={contact.email}
                      slotProps={{
                        primary: {
                          style: {
                            fontFamily: 'var(--font-title)',
                            fontWeight: 600,
                            fontSize: 16,
                          },
                        },
                        secondary: {
                          style: {
                            fontFamily: 'var(--font-title)',
                            fontWeight: 500,
                            fontSize: 14,
                            color: '#A4A8B0',
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                ))}
              </Menu>
            </div>
            <div
              className="flex flex-ac flex-jc-sb"
              style={{
                paddingTop: 34,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notify}
                    onChange={() => {
                      setNotify(!notify);
                      ls.set<boolean>('wiki.notify', !notify);
                    }}
                  />
                }
                label={
                  <span
                    className={styles.text}
                    style={{
                      fontSize: 14,
                      fontFamily: 'var(--font-title)',
                      fontWeight: 600,
                    }}
                  >
                    Notify people
                  </span>
                }
              />
              <div className="flex">
                <button
                  type="button"
                  onClick={() => {
                    setAddUserOpen(false);
                    setIsValid(false);
                    setPendingPermissions([]);
                    setValue('');
                    contactsMenu.close();
                    setTimeout(() => inputRef.current?.focus(), 1000);
                  }}
                  style={{
                    marginRight: 6,
                    color: theme === 'light' ? '#5f6368' : undefined,
                    borderRadius: 8,
                  }}
                  className={buttonStyles.transparent}
                >
                  Cancel
                </button>
                <ButtonSubmit className={buttonStyles.primaryWithIcon} disabled={pendingPermissions.length === 0} />
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};
